import { Link, useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Input, Layout, Space, Button, Table, Row, Modal, Drawer, Form, Switch, Checkbox, message, Col, Divider, Dropdown, Tooltip } from 'antd';
import './index.less';
import '../../../../styles.less';
import { arrayMoveImmutable } from 'array-move';
import useSortDragUserSelectChrome from '../../../../hooks/useSortDragUserSelectChrome';
import ProjectNameSortableRow from '../ProjectNameSortableRow';
import { solutionListApi, fetchContentModuleContents, changeContentModuleStatus, addContentModuleContents, removeContentModuleContents, sortContentModuleContents } from '@services/MedProject';
import config from '@config';
import { TABLE_PAGE_SIZE_OPTIONS } from '@/utils/property';
const { Search } = Input;

export default function ModulesSolutions() {
    const navigate = useNavigate();
    const params = useParams();
    const projectId = (params?.projectId && Number(params.projectId)) || 0;
    const moduleId = (params?.id && Number(params.id)) || 0;

    const listRequestIdRef = useRef(0);

    const [form] = Form.useForm();
    const [addForm] = Form.useForm();

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [open, setOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [recommendData, setRecommendData] = useState([]);

    const dndSensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 10 },
        }),
    );
    const { beginSortDrag, endSortDrag } = useSortDragUserSelectChrome();

    const [addPage, setAddPage] = useState(1);
    const [addSize, setAddSize] = useState(10);
    const [addTotal, setAddTotal] = useState(0);
    const [addSolutionData, setAddSolutionData] = useState([]);
    const [listLoading, setListLoading] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    const columns = [
        {
            title: '解决方案名称',
            dataIndex: 'projectName',
            key: 'projectName',
            render: text => <a>{text}</a>,
        },
        {
            title: '发布者昵称',
            key: 'nickName',
            render: (_, record) => <>{record.baseVO?.nickName}</>,
        },
        {
            title: '显示/隐藏',
            key: 'hidden',
            render: (_, record) => <Switch checked={!record.hidden} checkedChildren="显示" unCheckedChildren="隐藏" onChange={() => onHiddenChange(record)} />,
        },
        {
            title: '推荐到主页',
            key: 'recommended',
            render: (_, record) => <Switch checked={record.recommended} checkedChildren="推荐" unCheckedChildren="不推荐" onChange={() => onRecommendedChange(record)} />,
        },
        {
            title: '操作',
            dataIndex: 'operations',
            width: 150,
            render: (_, record) => {
                const items = [
                    {
                        label: '移除',
                        key: 'remove',
                        onClick: () => onDelete(record.id),
                    },
                ];

                return (
                    <Space className="operation-btns" separator={<Divider orientation="vertical" />}>
                        <Button size="small" type="link" onClick={() => viewSolution(record)}>
                            查看详情
                        </Button>
                        <Dropdown menu={{ items }} placement="bottomRight">
                            <Button type="link" size="small" className="more-operation">
                                <i className="bi bi-three-dots"></i>
                            </Button>
                        </Dropdown>
                    </Space>
                );
            },
        },
    ];

    const addColumns = [
        {
            title: 'ID',
            dataIndex: 'id',
            width: 100,
            align: 'center',
            key: 'id',
        },
        {
            title: '解决方案名称',
            dataIndex: 'title',
            key: 'title',
        },
    ];

    // 页码改变
    const onChangePage = (a, b) => {
        setPage(a);
        setPageSize(b);
    };

    const onShowSizeChange = (_, nextPageSize) => {
        setPage(1);
        setPageSize(nextPageSize);
    };

    const reloadSolutionsList = (nextPage = 1) => {
        if (nextPage !== page) {
            setPage(nextPage);
            return;
        }
        getSolutionsList();
    };

    // 项目解决方案名称搜索
    const solutionsSearch = e => {
        if (e?.target) {
            form.setFieldValue('name', e.target.value);
        }
        reloadSolutionsList(1);
    };

    // 项目解决方案推荐搜索
    const solutionsCheck = e => {
        if (e.target.checked) {
            form.setFieldValue('recommended', 1);
        } else {
            form.setFieldValue('recommended', undefined);
        }
        reloadSolutionsList(1);
    };

    // 显示/隐藏
    const onHiddenChange = async record => {
        if (record.recommended === 1 && record.hidden === 0) {
            Modal.confirm({
                title: '提示',
                icon: <i className="bi bi-exclamation-circle"></i>,
                centered: true,
                content: '当前解决方案已推荐到主页，设置隐藏后，项目主页上此解决方案也将进行隐藏。',
                okText: '确认',
                cancelText: '取消',
                onOk: async () => {
                    const res = await changeContentModuleStatus({ id: record.id, hidden: record.hidden ? 0 : 1 });
                    if (res.status_code === 200) {
                        message.success(res.message);
                        getSolutionsList();
                    }
                },
            });
        } else {
            const res = await changeContentModuleStatus({ id: record.id, hidden: record.hidden ? 0 : 1 });
            if (res.status_code === 200) {
                message.success(res.message);
                getSolutionsList();
            }
        }
    };

    // 推荐主页
    const onRecommendedChange = async record => {
        if (record.hidden === 1 && record.recommended === 0) {
            return message.error('当前解决方案已隐藏，不能推荐到首页，可以设置显示后再推荐。');
        }
        const res = await changeContentModuleStatus({ id: record.id, recommended: record.recommended ? 0 : 1 });
        if (res.status_code === 200) {
            message.success(res.message);
            getSolutionsList();
        }
    };

    // 移除
    const onDelete = contentId => {
        Modal.confirm({
            title: '提示',
            icon: <i className="bi bi-exclamation-circle"></i>,
            centered: true,
            content: '移除后，该解决方案在用户端也将下架，请确认是否移除？',
            okText: '确认',
            cancelText: '取消',
            onOk: async () => {
                const res = await removeContentModuleContents(contentId);
                if (res.status_code === 200) {
                    message.success('移除成功');
                    getSolutionsList();
                }
            },
        });
    };

    // 跳转详情
    const viewSolution = record => {
        return window.open(`${record?.baseVO?.jumpUrl}`);
    };

    // 添加解决方案显示
    const showAddModal = () => {
        setIsModalOpen(true);
        onSearch();
    };

    const handleOk = async () => {
        if (!selectedRowKeys.length) {
            return message.error('请选择关联解决方案');
        }
        const params = {
            contentType: 'solution',
            itemIds: [...selectedRowKeys],
            moduleId,
            projectId,
        };
        const res = await addContentModuleContents(params);
        if (res.status_code === 200) {
            message.success('新增成功');
            handleCancel();
            getSolutionsList();
        }
    };

    const handleCancel = () => {
        setIsModalOpen(false);
        addForm.resetFields();
        setSelectedRowKeys([]);
    };

    // 添加解决方案 - 搜索
    const onSearch = () => {
        addForm.validateFields().then(async values => {
            setListLoading(true);
            // console.log('addvalues:', values);
            const addPramas = {
                ...values,
                page: addPage,
                size: addSize,
                moduleId,
            };
            const res = await solutionListApi(addPramas);
            setListLoading(false);
            if (res && res.status_code === 200 && res.data) {
                setAddSolutionData(res.data.records || []);
                setAddTotal(res.data.total);
            }
        });
    };

    // 添加解决方案 - 页码改变
    const onChangeAddPage = (a, b) => {
        setAddPage(a);
        setAddSize(b);
    };

    const rowSelection = {
        preserveSelectedRowKeys: true,
        selectedRowKeys,
        onChange: (nowSelectedRowKeys, selectedRows) => {
            setSelectedRowKeys(nowSelectedRowKeys);
        },
        getCheckboxProps: record => ({ disabled: record.hasData }), // 禁用
    };

    const onSortModal = () => {
        getSolutionsList(1);
    };

    const onClose = () => {
        form.resetFields();
        getSolutionsList();
        setOpen(false);
    };

    const onDragEndSolutionsSort = useCallback(
        async event => {
            endSortDrag();
            const { active, over } = event;
            if (!over || active.id === over.id) {
                return;
            }
            const oldIndex = recommendData.findIndex(item => item.id === active.id);
            const newIndex = recommendData.findIndex(item => item.id === over.id);
            if (oldIndex === -1 || newIndex === -1) {
                return;
            }
            const newData = arrayMoveImmutable(recommendData, oldIndex, newIndex);
            const ids = newData.map(item => item.id);
            const res = await sortContentModuleContents([...ids]);
            if (res.status_code === 200) {
                message.success('排序成功');
                setRecommendData([...newData]);
            }
        },
        [recommendData, endSortDrag],
    );

    // 解决方案列表
    const getSolutionsList = async recommend => {
        if (!moduleId) {
            return;
        }

        const requestId = ++listRequestIdRef.current;
        const reqParams = {
            projectId,
            moduleId,
            page: recommend ? 1 : page,
            pageSize,
            name: form.getFieldValue('name'),
            recommended: recommend ? recommend : form.getFieldValue('recommended'),
            contentType: 'solution',
        };
        setLoading(true);
        let res;
        try {
            res = await fetchContentModuleContents(reqParams);
        } finally {
            if (requestId === listRequestIdRef.current) {
                setLoading(false);
            }
        }
        if (requestId !== listRequestIdRef.current) {
            return;
        }

        if (!res || typeof res !== 'object' || res.status_code !== 200 || !res.data) {
            if (!recommend) {
                setData([]);
                setTotal(0);
            }
            return;
        }

        const list = Array.isArray(res.data.list) ? res.data.list : Array.isArray(res.data.records) ? res.data.records : [];

        if (recommend) {
            if (list.length) {
                setRecommendData(list);
                setOpen(true);
            } else {
                message.warning('暂无首页推荐内容，无需排序');
            }
            return;
        }

        setData(list);
        setTotal(res.data.totalCount ?? res.data.total ?? 0);
    };

    useEffect(() => {
        listRequestIdRef.current += 1;
        setPage(1);
    }, [moduleId]);

    useEffect(() => {
        getSolutionsList();
    }, [page, pageSize, moduleId]);

    useEffect(() => {
        if (!isModalOpen) {
            return;
        }
        onSearch();
    }, [addPage, addSize, moduleId, isModalOpen]);

    return (
        <>
            <div className="solutions-page">
                <div className="med-project-module-head">
                    <div className="module-title">
                        <h2>解决方案</h2>
                        <span className="tips">
                            可以添加平台解决方案，<em>项目首页最多可推荐10条数据</em>
                        </span>
                    </div>
                    <div className="preview">
                        <Button
                            type="link"
                            size="small"
                            style={{ padding: 0 }}
                            onClick={() => window.open(`${config.PROJECT_DOMAIN}/p/${projectId}/solutions/${moduleId}`)}
                            icon={
                                <>
                                    <span className="anticon">
                                        <i className="bi bi-box-arrow-up-right"></i>
                                    </span>
                                </>
                            }
                        >
                            <span>预览</span>
                        </Button>
                    </div>
                </div>

                <div className="med-project-module-toolbar">
                    <div className="main-tools">
                        <Form form={form} layout="inline">
                            <Form.Item name="name" label="">
                                <Input placeholder="请输入解决方案名称" allowClear onPressEnter={solutionsSearch} />
                            </Form.Item>

                            <Form.Item name="recommended" label="">
                                <Checkbox onChange={solutionsCheck}>只看主页推荐</Checkbox>
                            </Form.Item>
                        </Form>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Tooltip title="为推荐首页内容排序">
                                <Button onClick={() => onSortModal()}>主页推荐排序</Button>
                            </Tooltip>
                            <Button type="primary" onClick={() => showAddModal()}>
                                添加解决方案
                            </Button>
                        </Space>
                    </div>
                </div>

                <div className="med-project-module-content">
                    <Table
                        rowKey="id"
                        columns={columns}
                        dataSource={data}
                        loading={loading}
                        bordered
                        size="middle"
                        pagination={{
                            total: total,
                            showTotal: () => `共${total}条`,
                            current: page,
                            pageSize: pageSize,
                            pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
                            onChange: onChangePage,
                            onShowSizeChange: onShowSizeChange,
                            showQuickJumper: true,
                            showSizeChanger: true,
                        }}
                    />
                </div>

                <Drawer title="解决方案排序" onClose={onClose} open={open} mask={{ closable: false }} size={600} footer={null} closable={{ placement: 'end' }}>
                    <div className="med-project-name-sort-list">
                        <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragStart={beginSortDrag} onDragCancel={endSortDrag} onDragEnd={onDragEndSolutionsSort}>
                            <SortableContext items={recommendData.map(item => item.id)} strategy={verticalListSortingStrategy}>
                                {recommendData.map(item => (
                                    <ProjectNameSortableRow key={item.id} id={item.id} name={item.projectName} />
                                ))}
                            </SortableContext>
                        </DndContext>
                    </div>
                </Drawer>

                <Modal title="添加解决方案" open={isModalOpen} className="add-solutions-modal" centered width={900} onOk={handleOk} onCancel={handleCancel}>
                    <Form form={addForm} name="basic" labelCol={{ span: 3 }} wrapperCol={{ span: 24 }} autoComplete="off">
                        <Row>
                            <Col span={6} style={{ marginRight: '10px' }}>
                                <Form.Item label="" name="solutionId">
                                    <Input placeholder="解决方案ID" />
                                </Form.Item>
                            </Col>
                            <Col span={6} style={{ marginRight: '10px' }}>
                                <Form.Item label="" name="title">
                                    <Input placeholder="解决方案名称" />
                                </Form.Item>
                            </Col>
                            <Col span={6} style={{ marginRight: '10px' }}>
                                <Button type="primary" onClick={onSearch}>
                                    搜索
                                </Button>
                            </Col>
                        </Row>
                    </Form>

                    <Table
                        rowKey={record => record.id}
                        rowSelection={{ ...rowSelection }}
                        columns={addColumns}
                        dataSource={addSolutionData}
                        loading={listLoading}
                        size="small"
                        pagination={{
                            total: addTotal,
                            showTotal: () => `共${addTotal}条`,
                            current: addPage,
                            pageSize: addSize,
                            pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
                            onChange: onChangeAddPage,
                            showQuickJumper: true,
                            showSizeChanger: true,
                        }}
                    />
                </Modal>
            </div>
        </>
    );
}
